import { StylesObject } from '../../types/styles'
import { SITE_VARIABLES } from '../../themes/variables'

interface GroupOfLinksPropsStylesObject extends StylesObject {
    wrapper: StylesObject
    linkWrapper: StylesObject
    link: StylesObject
    circle: StylesObject
}

export const LARGE_SCREEN_SIZE = 1080
export const SMALL_SCREEN_SIZE = 719

export const MEDIA_BREAKPOINTS = {
    large: `@media (min-width: ${LARGE_SCREEN_SIZE}px)`,
    small: `@media (max-width:${SMALL_SCREEN_SIZE}px)`,
}

const getSISwimsuitStyle = (siteVars: typeof SITE_VARIABLES, shouldRenderWithImages: boolean) => {
    const { TYPOGRAPHY, CSS_SELECTORS, COLORS } = siteVars
    return {
        wrapper: {
            ...(shouldRenderWithImages
                ? {
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 0fr)',
                      gap: '20px',
                      [MEDIA_BREAKPOINTS.small]: {
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '25px',
                      },
                  }
                : { flexDirection: 'column' }),
        },
        linkWithImage: {
            width: '206px',
            [MEDIA_BREAKPOINTS.small]: {
                height: '100%',
                width: '100%',
            },
            textDecoration: 'none',
            textAlign: 'start',
            fontWeight: TYPOGRAPHY.bigger.fontWeight,
            fontFamily: TYPOGRAPHY.bigger.fontFamily,
            fontSize: TYPOGRAPHY.bigger.screenSizes.large,
            color: '#212121',
            [CSS_SELECTORS.HOVER]: {
                color: COLORS.PRIMARY,
            },
        },
        image: {
            objectFit: 'cover',
            objectPosition: 'top',
            height: '116px',
            marginBottom: '10px',
            [MEDIA_BREAKPOINTS.small]: {
                height: '100%',
                width: '100%',
            },
        },
        imageAndTitleWrapper: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column-reverse',
        },
    }
}

const getSiCoreStyles = (siteVars: typeof SITE_VARIABLES) => {
    const { TYPOGRAPHY, COLORS, CSS_SELECTORS } = siteVars

    return {
        link: {
            textDecoration: 'none',
            textAlign: 'start',
            color: COLORS.PRIMARY,
            fontWeight: TYPOGRAPHY.bigger.fontWeight,
            fontFamily: TYPOGRAPHY.bigger.fontFamily,
            fontSize: TYPOGRAPHY.normal.screenSizes.large,
            [MEDIA_BREAKPOINTS.small]: {
                fontSize: TYPOGRAPHY.normal.screenSizes.medium,
            },
        },
        linkWithImage: {
            display: 'flex',
            justifyContent: 'space-between',
            margin: '15px 0px',
            textDecoration: 'none',
            textAlign: 'start',
            alignItems: 'center',
            color: COLORS.BLACK,
            [CSS_SELECTORS.HOVER]: {
                color: COLORS.PRIMARY,
            },
            height: '97px',
            fontWeight: TYPOGRAPHY.large.fontWeight,
            fontFamily: TYPOGRAPHY.large.fontFamily,
            fontSize: TYPOGRAPHY.normal.screenSizes.large,
            [MEDIA_BREAKPOINTS.small]: {
                height: '77px',
                fontWeight: TYPOGRAPHY.big.fontWeight,
                fontFamily: TYPOGRAPHY.big.fontFamily,
                fontSize: TYPOGRAPHY.normal.screenSizes.small,
            },
        },
    }
}

const getDefaultStyle = (siteVars: typeof SITE_VARIABLES) => {
    const { TYPOGRAPHY, COLORS, CSS_SELECTORS } = siteVars
    return {
        wrapper: {
            display: 'flex',
            flexDirection: 'column',
        },
        linkWithImage: {
            display: 'flex',
            justifyContent: 'space-between',
            margin: '15px 0px',
            textDecoration: 'none',
            textAlign: 'start',
            alignItems: 'center',
            color: COLORS.BLACK,
            [CSS_SELECTORS.HOVER]: {
                color: COLORS.PRIMARY,
            },
            height: '97px',
            fontWeight: TYPOGRAPHY.large.fontWeight,
            fontFamily: TYPOGRAPHY.large.fontFamily,
            fontSize: TYPOGRAPHY.large.screenSizes.large,
            [MEDIA_BREAKPOINTS.small]: {
                height: '77px',
                fontWeight: TYPOGRAPHY.big.fontWeight,
                fontFamily: TYPOGRAPHY.big.fontFamily,
                fontSize: TYPOGRAPHY.big.screenSizes.small,
            },
        },
        image: {
            marginLeft: '20px',
            width: '153px',
            [MEDIA_BREAKPOINTS.small]: {
                marginLeft: '10px',
                width: '120px',
            },
        },
        imageAndTitleWrapper: {
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            alignItems: 'center',
        },
    }
}

const getCommonStyles = (siteVars: typeof SITE_VARIABLES) => {
    const { COLORS, TYPOGRAPHY } = siteVars
    return {
        link: {
            textDecoration: 'none',
            textAlign: 'start',
            color: COLORS.PRIMARY,
            fontWeight: TYPOGRAPHY.bigger.fontWeight,
            fontFamily: TYPOGRAPHY.bigger.fontFamily,
            fontSize: TYPOGRAPHY.bigger.screenSizes.large,
            [MEDIA_BREAKPOINTS.small]: {
                fontSize: TYPOGRAPHY.bigger.screenSizes.medium,
            },
        },
        circle: {
            color: COLORS.PRIMARY,
            marginRight: '8px',
        },
        linkWrapper: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '10px',
        },
        divider: {
            height: '1px',
            backgroundColor: '#D0C8C1',
        },
    }
}

export const getDefaultGroupOfLinksStylesObj = (
    siteVars: typeof SITE_VARIABLES,
    isSwimsuit: boolean,
    shouldRenderWithImages: boolean,
    isSI: boolean,
): GroupOfLinksPropsStylesObject => {
    return {
        ...getCommonStyles(siteVars),
        ...(isSwimsuit ? getSISwimsuitStyle(siteVars, shouldRenderWithImages) : getDefaultStyle(siteVars)),
        ...(isSI && !isSwimsuit ? getSiCoreStyles(siteVars) : {}),
    }
}

export const getExpStyles = (siteVars: typeof SITE_VARIABLES) => {
    const { COLORS } = siteVars
    return {
        header: {
            fontSize: '16px',
            fontFamily: 'Montserrat, sans-serif',
            lineHeight: '1.375em',
            fontWeight: 700,
            borderTop: `4px solid ${COLORS.PRIMARY}`,
            padding: '15px 0 10px',

            [MEDIA_BREAKPOINTS.large]: {
                fontSize: '24px',
            },
        },
        row: {
            marginBottom: '10px',
            borderRadius: '6px',

            '&:hover': {
                backgroundColor: '#F5F5F5',
            },
        },
        card: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
        },
        image: {
            width: '100px',
            height: '100px',
            borderRadius: '6px',
            objectFit: 'cover',
            flexShrink: 0,

            [MEDIA_BREAKPOINTS.large]: {
                width: '120px',
                height: '90px',
            },
        },
        textBlock: {
            padding: '0 10px',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 700,
            fontSize: '16px',
            lineHeight: '1.375em',
            color: COLORS.BLUE,
            display: 'flex',
            alignItems: 'flex-start',
        },
        link: {
            textDecoration: 'none',
        },
        number: {
            fontSize: '16px',
            fontFamily: 'Montserrat, sans-serif',
            lineHeight: '1.375em',
            fontWeight: 700,
            backgroundColor: COLORS.PRIMARY,
            color: COLORS.WHITE,
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
    }
}
